import { useRef, useState } from "react";
import { Button, Card, FileButton, Group, Select, Stack, Text, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { submitTranscript, uploadCandidateSource } from "../api";
import { transcriptTypeKeys } from "../_Shared/transcriptTypes";

const TRANSCRIPT_FILE_TYPES = ".docx,.doc,.pdf,.rtf,.txt,.vtt";

// Paste a transcript or notes, or upload one as a file (Word, PDF, text, Teams/Zoom .vtt).
// Both take the transcript path: extraction onto the contact plus the project-aware analysis.
// projectId files the conversation for one search project, so the analysis attributes it there
// instead of guessing the project from the text.
export default function TranscriptForm({ candidateId, projectId = null, onSubmitted, onCancel }) {
  const { t } = useTranslation("Veft");
  const [text, setText] = useState("");
  const [transcriptType, setTranscriptType] = useState("interview");
  const [busy, setBusy] = useState(false);
  const resetFileRef = useRef(null);

  const transcriptTypeOptions = Object.entries(transcriptTypeKeys)
    .map(([value, key]) => ({ value, label: t(key) }));

  async function run(action, failureKey) {
    setBusy(true);
    try {
      await action();
      notifications.show({
        title: t("transcript_submitted"),
        message: t(projectId ? "transcript_project_submitted_msg" : "transcript_submitted_msg"),
        color: "green",
      });
      setText("");
      onSubmitted?.();
    } catch {
      notifications.show({ title: t("error"), message: t(failureKey), color: "red" });
    } finally {
      setBusy(false);
      resetFileRef.current?.();
    }
  }

  function submitText() {
    if (!text.trim()) return;
    run(() => submitTranscript(candidateId, { text: text.trim(), transcriptType, projectId }), "error");
  }

  function submitFile(file) {
    if (!file) return;
    run(() => uploadCandidateSource(candidateId, file, "MeetingTranscript", projectId), "transcript_upload_failed");
  }

  return (
    <Card p="sm" withBorder>
      <Stack gap="xs">
        <Select size="xs" value={transcriptType} onChange={setTranscriptType}
          data={transcriptTypeOptions} style={{ width: 160 }} allowDeselect={false} />
        <Textarea
          placeholder={t("paste_transcript")}
          autosize minRows={6} maxRows={20}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <Group justify="space-between" gap="xs">
          <Group gap="xs">
            <FileButton onChange={submitFile} accept={TRANSCRIPT_FILE_TYPES} resetRef={resetFileRef}>
              {(props) => (
                <Button {...props} size="xs" variant="subtle" leftSection={<IconUpload size={14} />} disabled={busy}>
                  {t("transcript_file_pick")}
                </Button>
              )}
            </FileButton>
            <Text size="xs" c="dimmed">{t("transcript_file_hint")}</Text>
          </Group>
          <Group gap="xs">
            {onCancel && (
              <Button size="xs" variant="subtle" color="gray" onClick={() => { setText(""); onCancel(); }}>
                {t("cancel")}
              </Button>
            )}
            <Button size="xs" loading={busy} disabled={!text.trim()} onClick={submitText}>
              {t("submit_analyze")}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}
